import type {
  AddToSequenceConfig,
  CrmActionConfig,
  CustomNodeConfig,
  CustomNodeType,
  ExportConfig,
  NotifyConfig,
  SendLinkedInActionConfig,
  SendMailboxEmailConfig,
  TriggerConfig,
} from '@/types/custom-playbooks';
import { cleanupCrmType } from './cleanup-rules';
import {
  CRM_TARGET_LABELS,
  crmActionOptions,
  crmWriteSummary,
  isCrmWriteAction,
} from './config/crm-write-config';
import { syncOwnerWords } from './config/sync-owner-config.util';
import {
  crmLabel,
  crmObjectLabel,
  nodeLabel,
  nodeSummary,
} from './node-metadata';
import {
  sequenceChangeWords,
  sequenceStepSummary,
  withProvider,
} from './sequence-step.util';
import { sendMailboxEmailHeadlineWords } from './config/mailbox-email.util';
import {
  LINKEDIN_ACTION_WORDS,
  linkedInActionOf,
  sendLinkedInActionHeadlineWords,
} from './config/linkedin-action.util';
import { triggerLogoOf } from './trigger-kinds';
import { BRAND } from '@/lib/brand';

/**
 * The play builder's connectors: every tool a step talks to is a row of its
 * own, beside Cleanlist's own steps. A row is a step type plus a preset
 * patch; nothing new is stored, so a saved step finds its row from its type
 * and one config key (`connectorOf`), and saved plays, runs and Clu
 * proposals need no migration.
 *
 * `node-metadata` is read only inside functions, never while this module
 * loads, so that module can call into this one.
 */

/** A tool a step talks to, by the API's own value. */
export type ConnectorId =
  | 'cleanlist'
  | 'lemlist'
  | 'outreach'
  | 'salesloft'
  | 'nooks'
  | 'instantly'
  | 'salesforce'
  | 'hubspot'
  | 'attio'
  | 'slack'
  | 'teams'
  | 'email'
  | 'gmail'
  | 'outlook'
  | 'linkedin'
  | 'google_sheets'
  | 'google_docs'
  | 'google_calendar'
  | 'fireflies'
  | 'fathom'
  | 'gong'
  | 'notion';

export type ConnectorSection =
  | 'data'
  | 'meetings'
  | 'ai'
  | 'crm'
  | 'sequencing'
  | 'communications'
  | 'docs'
  | 'logic'
  | 'developer';

/** The picker's sections, in the order data flows through a play. */
export const CONNECTOR_SECTIONS: readonly {
  id: ConnectorSection;
  label: string;
}[] = [
  { id: 'data', label: 'Data' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'ai', label: 'AI' },
  { id: 'crm', label: 'CRM' },
  { id: 'sequencing', label: 'Sequencing' },
  { id: 'communications', label: 'Communications' },
  { id: 'docs', label: 'Docs and notes' },
  { id: 'logic', label: 'Logic' },
  { id: 'developer', label: 'Developer' },
];

/** Whose connection a row's state reads: the org's CRM, an outside
 *  sequencer's key or sign in, Slack, or a workspace provider's. */
export type ConnectionSource =
  | 'crm'
  | 'sequencer'
  | 'slack'
  | 'teams'
  | 'workspace';

/** What hides a row beyond its step type's own gate (`useHiddenStepTypes`):
 *  the Cleanlist, Gmail and Outlook rows follow `useSequenceStepAccess`, an
 *  outside sequencer `useOutsideSequencerAccess`. */
export type ConnectorGate = 'sequencing' | 'outside_sequencer';

/** Merged over the step type's default config when the step is added;
 *  never a whole config. */
export type ConnectorPreset = Partial<CustomNodeConfig>;

export interface ConnectorDef {
  /** Unique: the picker row's key. A tool with one row goes by its own id. */
  id: string;
  section: ConnectorSection;
  /** The tool's name, or the step's own for Cleanlist's steps. */
  name: string;
  /** What the row does: after the name where one tool has several rows, and
   *  on the card until the step's target is picked. */
  action: string;
  /** What the step does in a short phrase (under 45 characters, no
   *  dashes): the picker card's line where no connection state applies. */
  description: string;
  stepType: CustomNodeType;
  preset?: ConnectorPreset;
  /** Whether a step of `stepType` with this config is this row. Absent on
   *  a row that is its step type's only one. */
  matches?: (config: Partial<CustomNodeConfig>) => boolean;
  /** The tool, whose logo the tile shows; null keeps the step's glyph. */
  tool: ConnectorId | null;
  gate: ConnectorGate | null;
  connection: ConnectionSource | null;
  /** More words the picker's search matches. */
  keywords: readonly string[];
}

/** One of Cleanlist's own steps: its glyph, no tool and no connection. */
function step(
  stepType: CustomNodeType,
  section: ConnectorSection,
  name: string,
  description: string,
  keywords: readonly string[]
): ConnectorDef {
  return {
    id: stepType,
    section,
    name,
    action: name,
    description,
    stepType,
    tool: null,
    gate: null,
    connection: null,
    keywords,
  };
}

const callNotesKeywords = ['call', 'notes', 'transcript', 'meeting'] as const;

const crmKeywords = (crmType: CrmActionConfig['crmType']) => [
  'crm',
  ...crmActionOptions(crmType, false).map((option) =>
    option.label.toLowerCase()
  ),
];

/** Every row the builder can insert, in section order. */
export const CONNECTOR_ROWS: readonly ConnectorDef[] = [
  step('find_people', 'data', 'Find People', 'Find people at each company', [
    'people',
    'contacts',
    'prospects',
    'search',
  ]),
  step(
    'get_company',
    'data',
    'Get company',
    'Turn people into one record per company',
    ['company', 'account', 'employer']
  ),
  step('find_open_jobs', 'data', 'Find open jobs', '', [
    'company',
    'hiring',
    'openings',
    'jobs',
  ]),
  step('find_reviews', 'data', 'Find reviews', '', [
    'company',
    'software',
    'reviews',
    'rating',
  ]),
  step('enrich', 'data', 'Enrich', 'Find emails, phones and profile data', [
    'email',
    'phone',
    'profile',
  ]),
  step('validate', 'data', 'Validate', 'Verify emails and phones', [
    'verify',
    'email',
    'phone',
  ]),
  // The employment steps, agent plays only (useHiddenStepTypes).
  step(
    'identify_person',
    'data',
    'Identify person',
    'Find each person’s LinkedIn profile',
    ['linkedin', 'identify', 'match person']
  ),
  step(
    'job_history',
    'data',
    'Job history',
    'Read each person’s LinkedIn job history',
    ['employment', 'jobs', 'left', 'linkedin']
  ),
  step(
    'match_company',
    'data',
    'Match company',
    'Check the account against job history',
    ['account', 'still there', 'left', 'employer check']
  ),
  // Agent plays only (useHiddenStepTypes); free.
  step(
    'count_people',
    'data',
    'Count people',
    'Count matching people at each company',
    ['count', 'how many', 'people', 'audience']
  ),
  {
    id: 'google_calendar',
    section: 'meetings',
    name: 'Google Calendar',
    action: 'Find meetings',
    description: 'Find the next and last meeting',
    stepType: 'find_meetings',
    tool: 'google_calendar',
    gate: null,
    connection: 'workspace',
    keywords: ['calendar', 'meetings', 'google'],
  },
  {
    id: 'fireflies',
    section: 'meetings',
    name: 'Fireflies',
    action: 'Get call notes',
    description: 'Read call summaries and action items',
    stepType: 'fireflies_notes',
    tool: 'fireflies',
    gate: null,
    connection: 'workspace',
    keywords: callNotesKeywords,
  },
  {
    id: 'fathom',
    section: 'meetings',
    name: 'Fathom',
    action: 'Get call notes',
    description: 'Read call summaries and action items',
    stepType: 'fathom_notes',
    tool: 'fathom',
    gate: null,
    connection: 'workspace',
    keywords: callNotesKeywords,
  },
  {
    id: 'gong',
    section: 'meetings',
    name: 'Gong',
    action: 'Get call notes',
    description: 'Read call summaries and action items',
    stepType: 'gong_notes',
    tool: 'gong',
    gate: null,
    connection: 'workspace',
    keywords: callNotesKeywords,
  },
  step('research', 'ai', 'Agents', 'Run saved AI agents on each record', [
    'ai',
    'research',
    'qualify',
    'write',
  ]),
  {
    id: 'salesforce',
    section: 'crm',
    name: 'Salesforce',
    action: 'Sync',
    description: 'Create, update and read records',
    stepType: 'crm_action',
    preset: { crmType: 'salesforce' },
    // No CRM saved reads as Salesforce, the step's default.
    matches: (config) =>
      ((config as Partial<CrmActionConfig>).crmType ?? 'salesforce') ===
      'salesforce',
    tool: 'salesforce',
    gate: null,
    connection: 'crm',
    keywords: crmKeywords('salesforce'),
  },
  // The step's default object is Contact; the job change override and
  // `defaultNewAccountSync` set the rest, so the preset never names one
  // (and never Lead, which HubSpot fails at run time).
  {
    id: 'hubspot',
    section: 'crm',
    name: 'HubSpot',
    action: 'Sync',
    description: 'Create, update and read records',
    stepType: 'crm_action',
    preset: { crmType: 'hubspot' },
    matches: (config) =>
      (config as Partial<CrmActionConfig>).crmType === 'hubspot',
    tool: 'hubspot',
    gate: null,
    connection: 'crm',
    keywords: crmKeywords('hubspot'),
  },
  // Offered only when /crm/options lists Attio (`useOfferedConnectors`). Its
  // match names Attio alone, so a CRM no row names (Pipedrive) gets no row.
  {
    id: 'attio',
    section: 'crm',
    name: 'Attio',
    action: 'Sync',
    description: 'Create, update and read records',
    stepType: 'crm_action',
    preset: { crmType: 'attio' },
    matches: (config) =>
      (config as Partial<CrmActionConfig>).crmType === 'attio',
    tool: 'attio',
    gate: null,
    connection: 'crm',
    keywords: crmKeywords('attio'),
  },
  step(
    'review_duplicates',
    'crm',
    'Review duplicates',
    'Show duplicate groups for review',
    ['duplicates', 'dedupe', 'cleanup']
  ),
  step(
    'merge_duplicates',
    'crm',
    'Merge duplicates',
    'Merge reviewed groups in your CRM',
    ['duplicates', 'dedupe', 'cleanup']
  ),
  // No provider saved is Cleanlist, the step as it always was.
  {
    id: 'cleanlist',
    section: 'sequencing',
    name: BRAND.name,
    action: 'Add to sequence',
    description: 'Add people to a sequence',
    stepType: 'add_to_sequence',
    matches: (config) =>
      ((config as Partial<AddToSequenceConfig>).provider ?? 'cleanlist') ===
      'cleanlist',
    tool: 'cleanlist',
    gate: 'sequencing',
    connection: null,
    keywords: ['sequence', 'email', 'campaign'],
  },
  {
    id: 'lemlist',
    section: 'sequencing',
    name: 'lemlist',
    action: 'Add to campaign',
    description: 'Add people to a campaign',
    stepType: 'add_to_sequence',
    preset: withProvider({}, 'lemlist'),
    matches: (config) =>
      (config as Partial<AddToSequenceConfig>).provider === 'lemlist',
    tool: 'lemlist',
    gate: 'outside_sequencer',
    connection: 'sequencer',
    keywords: ['campaign', 'sequence'],
  },
  {
    id: 'outreach',
    section: 'sequencing',
    name: 'Outreach',
    action: 'Add to sequence',
    description: 'Add people to a sequence',
    stepType: 'add_to_sequence',
    preset: withProvider({}, 'outreach'),
    matches: (config) =>
      (config as Partial<AddToSequenceConfig>).provider === 'outreach',
    tool: 'outreach',
    gate: 'outside_sequencer',
    connection: 'sequencer',
    keywords: ['sequence'],
  },
  {
    id: 'salesloft',
    section: 'sequencing',
    name: 'Salesloft',
    action: 'Add to cadence',
    description: 'Add people to a cadence',
    stepType: 'add_to_sequence',
    preset: withProvider({}, 'salesloft'),
    matches: (config) =>
      (config as Partial<AddToSequenceConfig>).provider === 'salesloft',
    tool: 'salesloft',
    gate: 'outside_sequencer',
    connection: 'sequencer',
    keywords: ['cadence', 'sequence'],
  },
  // Nooks creates people from Salesforce or HubSpot records only, so it adds
  // only people an earlier CRM step matched (the rest skip "Not in CRM").
  // Offered behind nooks_v1 (`useNooksOffered`).
  {
    id: 'nooks',
    section: 'sequencing',
    name: 'Nooks AI',
    action: 'Add to sequence',
    description: 'Add CRM people to a dialer sequence',
    stepType: 'add_to_sequence',
    preset: withProvider({}, 'nooks'),
    matches: (config) =>
      (config as Partial<AddToSequenceConfig>).provider === 'nooks',
    tool: 'nooks',
    gate: 'outside_sequencer',
    connection: 'sequencer',
    keywords: ['sequence', 'dialer', 'call', 'phone'],
  },
  // Instantly runs the campaign; the step adds leads with the play values
  // the campaign declares as custom variables. Offered behind instantly_v1.
  {
    id: 'instantly',
    section: 'sequencing',
    name: 'Instantly',
    action: 'Add to campaign',
    description: 'Add people to a campaign',
    stepType: 'add_to_sequence',
    preset: withProvider({}, 'instantly'),
    matches: (config) =>
      (config as Partial<AddToSequenceConfig>).provider === 'instantly',
    tool: 'instantly',
    gate: 'outside_sequencer',
    connection: 'sequencer',
    keywords: ['campaign', 'sequence', 'cold email'],
  },
  // Holds people until the Instantly inboxes below have room. Its own step,
  // not a tool row: the API names it by its label, and it wears Instantly's
  // logo (`nodeLogoOf`). Agent plays only (useHiddenStepTypes).
  step(
    'instantly_capacity',
    'sequencing',
    'Instantly capacity',
    'Wait for room in your Instantly inboxes',
    ['capacity', 'inboxes', 'room', 'limit', 'throttle']
  ),
  // No channel saved is Slack, the step's default.
  {
    id: 'slack',
    section: 'communications',
    name: 'Slack',
    action: 'Send a message',
    description: 'Post to a channel or DM',
    stepType: 'notify',
    matches: (config) =>
      ((config as Partial<NotifyConfig>).channel ?? 'slack') === 'slack',
    tool: 'slack',
    gate: null,
    connection: 'slack',
    keywords: ['notify', 'message', 'channel', 'alert'],
  },
  {
    id: 'teams',
    section: 'communications',
    name: 'Microsoft Teams',
    action: 'Send a message',
    description: 'Post to a channel or DM',
    stepType: 'notify',
    preset: { channel: 'teams' },
    matches: (config) => (config as Partial<NotifyConfig>).channel === 'teams',
    tool: 'teams',
    gate: null,
    connection: 'teams',
    keywords: [
      'notify',
      'message',
      'channel',
      'alert',
      'microsoft',
      'clu',
      'dm',
    ],
  },
  {
    id: 'email',
    section: 'communications',
    name: 'Email',
    action: 'Send an email',
    description: 'Email the play owner or record owners',
    stepType: 'notify',
    preset: { channel: 'email' },
    matches: (config) => (config as Partial<NotifyConfig>).channel === 'email',
    tool: 'email',
    gate: null,
    connection: null,
    keywords: ['notify', 'message', 'mail', 'alert'],
  },
  // One email a person from the member's own mailbox: a row per provider.
  {
    id: 'gmail',
    section: 'communications',
    name: 'Gmail',
    action: 'Send an email',
    description: 'Email each person from your inbox',
    stepType: 'send_mailbox_email',
    preset: { provider: 'google_workspace' },
    matches: (config) =>
      (config as Partial<SendMailboxEmailConfig>).provider ===
      'google_workspace',
    tool: 'gmail',
    gate: 'sequencing',
    connection: null,
    keywords: ['email', 'mail', 'send', 'google', 'inbox'],
  },
  {
    id: 'outlook',
    section: 'communications',
    name: 'Outlook',
    action: 'Send an email',
    description: 'Email each person from your inbox',
    stepType: 'send_mailbox_email',
    preset: { provider: 'microsoft_365' },
    matches: (config) =>
      (config as Partial<SendMailboxEmailConfig>).provider === 'microsoft_365',
    tool: 'outlook',
    gate: 'sequencing',
    connection: null,
    keywords: ['email', 'mail', 'send', 'microsoft', 'inbox'],
  },
  // One message or invitation a person from the member's own LinkedIn
  // account (useHiddenStepTypes gates it on the plan).
  {
    id: 'linkedin',
    section: 'communications',
    name: 'LinkedIn',
    action: 'Send a message',
    description: 'Message or invite each person',
    stepType: 'send_linkedin_action',
    tool: 'linkedin',
    gate: null,
    connection: null,
    keywords: ['message', 'invite', 'invitation', 'connect', 'send'],
  },
  {
    id: 'google_sheets',
    section: 'docs',
    name: 'Google Sheets',
    action: 'Add rows',
    description: 'Add or update rows in a Google Sheet',
    stepType: 'export',
    // Exports to a Cleanlist list do not use Google Sheets.
    matches: (config) =>
      !['lead_list', 'company_list'].includes(
        (config as Partial<ExportConfig>).destination ?? ''
      ),
    tool: 'google_sheets',
    gate: null,
    connection: 'workspace',
    keywords: ['sheets', 'spreadsheet', 'export', 'rows', 'google'],
  },
  {
    id: 'google_docs',
    section: 'docs',
    name: 'Google Docs',
    action: 'Create a doc',
    description: 'Create a doc for each record',
    stepType: 'create_google_doc',
    tool: 'google_docs',
    gate: null,
    connection: 'workspace',
    keywords: ['docs', 'document', 'google'],
  },
  {
    id: 'notion_add_page',
    section: 'docs',
    name: 'Notion',
    action: 'Add a page',
    description: 'Add a page for each record',
    stepType: 'notion_add_page',
    tool: 'notion',
    gate: null,
    connection: 'workspace',
    keywords: ['page', 'database', 'notes'],
  },
  {
    id: 'notion_update_page',
    section: 'docs',
    name: 'Notion',
    action: 'Update a page',
    description: 'Update the matching page for each record',
    stepType: 'notion_update_page',
    tool: 'notion',
    gate: null,
    connection: 'workspace',
    keywords: ['page', 'database', 'notes'],
  },
  step('branch', 'logic', 'Branch', 'Route records down different paths', [
    'if',
    'condition',
    'split',
    'route',
  ]),
  step('approval', 'logic', 'Approve', 'Ask a person before records continue', [
    'approval',
    'review',
  ]),
  step('wait', 'logic', 'Wait', 'Resume each record after a time or date', [
    'delay',
    'pause',
  ]),
  step('run_play', 'logic', 'Hand off', 'Send records to another play', [
    'handoff',
    'play',
  ]),
  step('webhook', 'developer', 'Webhook', 'Send records to an https endpoint', [
    'http',
    'api',
    'endpoint',
  ]),
  step('code', 'developer', 'Code', 'Compute outputs from record fields', [
    'javascript',
    'script',
  ]),
];

/**
 * The row a step is: the one row of its type, or the one its config key
 * picks (`provider`, `crmType`, `channel`). Null for a step this build
 * doesn't know, or one holding a value no row names (an outside sequencer
 * added after this build), which keeps today's label and glyph.
 */
export function connectorOf(
  type: string,
  config: Partial<CustomNodeConfig> | undefined
): ConnectorDef | null {
  for (const row of CONNECTOR_ROWS)
    if (row.stepType === type && (!row.matches || row.matches(config ?? {})))
      return row;
  return null;
}

/** Cleanlist's own data steps, which wear Cleanlist's mark. The Agents step
 *  wears Clu's; logic and developer steps keep their glyph. */
const CLEANLIST_STEPS: ReadonlySet<string> = new Set([
  'find_people',
  'get_company',
  'find_open_jobs',
  'find_reviews',
  'enrich',
  'validate',
  'count_people',
]);

/** A mark a tile can show: a tool's logo, or Clu's for the Agents step. */
export type NodeLogo = ConnectorId | 'clu';

/** The mark a step's tile shows: its tool's logo, the CRM the trigger
 *  checks for Review and Merge duplicates, the trigger's own mark, Clu's for
 *  Agents, Instantly's for the Instantly capacity gate, Cleanlist's for
 *  Cleanlist's own steps, else none (its glyph). */
export function nodeLogoOf(
  type: string,
  config: Partial<CustomNodeConfig> | undefined,
  trigger?: Partial<TriggerConfig>
): NodeLogo | null {
  if (type === 'trigger')
    return triggerLogoOf(config as Partial<TriggerConfig> | undefined);
  if (type === 'research') return 'clu';
  if (type === 'instantly_capacity') return 'instantly';
  // A company list is one of the organization's Cleanlist lists.
  if (
    type === 'export' &&
    (config as Partial<ExportConfig> | undefined)?.destination ===
      'company_list'
  )
    return 'cleanlist';
  if (type === 'review_duplicates' || type === 'merge_duplicates')
    return cleanupCrmType(trigger) ?? null;
  return (
    connectorOf(type, config)?.tool ??
    (CLEANLIST_STEPS.has(type) ? 'cleanlist' : null)
  );
}

/** A row's name in the picker, with its action where its tool has several
 *  rows: "Notion · Add a page", but "Salesloft". */
export function connectorRowLabel(row: ConnectorDef): string {
  const shared =
    row.tool !== null &&
    CONNECTOR_ROWS.some(
      (other) => other.id !== row.id && other.tool === row.tool
    );
  return shared ? `${row.name} · ${row.action}` : row.name;
}

/** The words the picker's search matches a row on: its section, name,
 *  action and keywords, then its id, which keeps each value unique. */
export function connectorSearchValue(row: ConnectorDef): string {
  const section = CONNECTOR_SECTIONS.find((s) => s.id === row.section);
  return [section?.label, row.name, row.action, ...row.keywords, row.id]
    .filter(Boolean)
    .join(' ');
}

/** cmdk's filter for the picker: a row matches when every word typed starts
 *  a word of its search value ("hubspot task", "add to cad"). cmdk's own
 *  fuzzy match chains letters across a row's keywords into stray hits. */
export function connectorSearchScore(value: string, search: string): number {
  const words = (text: string) =>
    text
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  const own = words(value);
  return words(search).every((typed) =>
    own.some((word) => word.startsWith(typed))
  )
    ? 1
    : 0;
}

/** The two gates a row can carry beyond its step type's own. */
export interface ConnectorAccess {
  /** Cleanlist sequencing, `useSequenceStepAccess`. */
  sequencing: boolean;
  /** Outside sequencers on an agent's play, `useOutsideSequencerAccess`. */
  outside: boolean;
  /** The Nooks row, `useNooksOffered`; absent hides it. */
  nooks?: boolean;
  /** The Attio row, when `/crm/options` lists Attio; absent hides it. */
  attio?: boolean;
  /** The Instantly row, `instantly_v1`; absent hides it. */
  instantly?: boolean;
  /** The Microsoft Teams row, `teams_notify_v1`; absent hides it. */
  teams?: boolean;
}

/**
 * The rows the picker offers at one spot: a step type the spot takes
 * (`addableStepTypesAt`) and the org may add (`useHiddenStepTypes`), past
 * the row's own gate. Rows leave here, before grouping, so no section is
 * left with a heading and nothing under it.
 */
export function offeredConnectorRows(
  stepTypes: readonly CustomNodeType[],
  hidden: ReadonlySet<CustomNodeType>,
  access: ConnectorAccess
): ConnectorDef[] {
  const allowed = new Set(stepTypes);
  return CONNECTOR_ROWS.filter(
    (row) =>
      allowed.has(row.stepType) &&
      !hidden.has(row.stepType) &&
      (row.gate !== 'sequencing' || access.sequencing) &&
      (row.gate !== 'outside_sequencer' || access.outside) &&
      (row.tool !== 'nooks' || access.nooks === true) &&
      (row.tool !== 'attio' || access.attio === true) &&
      (row.tool !== 'instantly' || access.instantly === true) &&
      (row.tool !== 'teams' || access.teams === true)
  );
}

/** Rows under their sections, in section order, leaving out a section with
 *  no row. */
export function connectorSections(rows: readonly ConnectorDef[]): {
  id: ConnectorSection;
  label: string;
  rows: ConnectorDef[];
}[] {
  return CONNECTOR_SECTIONS.map((section) => ({
    ...section,
    rows: rows.filter((row) => row.section === section.id),
  })).filter((section) => section.rows.length > 0);
}

/** What a step does in a word or two: a CRM step's saved action ("Create
 *  task", "Add to list" on HubSpot), else its row's. */
function connectorAction(
  row: ConnectorDef,
  config: Partial<CustomNodeConfig> | undefined
): string {
  if (row.stepType === 'send_linkedin_action') {
    const action = linkedInActionOf(config);
    return action ? LINKEDIN_ACTION_WORDS[action] : row.action;
  }
  if (row.stepType !== 'crm_action') return row.action;
  const c = (config ?? {}) as Partial<CrmActionConfig>;
  return (
    crmActionOptions(c.crmType ?? 'salesforce', false).find(
      (option) => option.value === c.action
    )?.label ?? row.action
  );
}

/** A CRM step's words after its CRM's name: "Sync Contact", "Look up
 *  Company", "Update 3 fields", "Create task · record owner". An object
 *  type of "default" (the CRM settings' record type) adds no word, as the
 *  API's step_headline. */
function crmWords(config: CrmActionConfig): string {
  if (isCrmWriteAction(config.action)) return crmWriteSummary(config, false);
  const verb =
    config.action === 'lookup'
      ? 'Look up'
      : config.target === 'new_record'
        ? 'Create'
        : 'Sync';
  const object =
    config.objectType === 'default'
      ? ''
      : crmObjectLabel(config.crmType, config.objectType);
  const words = [verb, object].filter(Boolean).join(' ');
  const owner =
    config.action === 'sync' ? syncOwnerWords(config.assignment) : null;
  return owner ? `${words} · ${owner}` : words;
}

/** Who a Notify step reaches, once that is set. */
function notifyTarget(config: NotifyConfig): string | null {
  if (config.channel === 'teams') {
    if (config.webhookId) return config.channelName || null;
    if (config.to === 'play_owner')
      return config.memberId !== undefined
        ? 'Teammate’s Teams DM'
        : 'Agent owner’s Teams DM';
    if (config.teamsChannelId)
      return config.to === 'record_owner'
        ? 'Record owner'
        : config.channelName || config.teamsChannelId;
    return null;
  }
  if (config.to === 'record_owner') return 'Record owner';
  if (config.channel === 'email')
    return config.deliver === 'list' || config.deliver === 'per_record'
      ? 'Recipients'
      : 'Playbook owner';
  return config.channelId ? `#${config.channelName || config.channelId}` : null;
}

/**
 * A connector step's card headline: the tool's name, then its target once
 * picked, else its row's action. "Salesloft · Add to cadence", then
 * "Salesloft · EMEA VP Sales"; "Slack · #sales"; "HubSpot · Sync Contact";
 * "Gmail · rep@acme.com".
 * Review and Merge duplicates lead with the CRM their trigger checks. Null
 * for any other step, which keeps `nodeSummary`.
 */
export function connectorHeadline(
  type: string,
  config: CustomNodeConfig,
  /** The play's trigger, for the CRM Review and Merge duplicates work on. */
  trigger?: Partial<TriggerConfig>
): string | null {
  if (type === 'review_duplicates' || type === 'merge_duplicates') {
    const crm = cleanupCrmType(trigger);
    return crm
      ? `${crmLabel(crm)} · ${nodeSummary(type, config, trigger)}`
      : null;
  }
  const row = connectorOf(type, config);
  if (!row?.tool) return null;
  switch (row.stepType) {
    case 'add_to_sequence': {
      const c = config as AddToSequenceConfig;
      const picked = c.sequenceId ? c.sequenceName?.trim() : '';
      // A Cleanlist pause or remove step leads with its action once picked:
      // "Cleanlist · Remove from Q4 outbound".
      const words = picked ? (sequenceChangeWords(c, picked) ?? picked) : '';
      return `${row.name} · ${words || row.action}`;
    }
    case 'notify':
      return `${row.name} · ${notifyTarget(config as NotifyConfig) ?? row.action}`;
    case 'crm_action':
      return `${row.name} · ${crmWords(config as CrmActionConfig)}`;
    case 'send_mailbox_email':
      return `${row.name} · ${sendMailboxEmailHeadlineWords(config as SendMailboxEmailConfig, row.action)}`;
    case 'send_linkedin_action':
      return `${row.name} · ${sendLinkedInActionHeadlineWords(config as SendLinkedInActionConfig)}`;
    default:
      return null;
  }
}

/**
 * A connector step's card detail, the line under its headline, so every card
 * reads as two lines: what the step's settings add to the headline, else its
 * row's action, never its type's description. "Salesloft · EMEA VP Sales"
 * over "Add to cadence"; "Salesloft · Add to cadence" over "Pick a
 * sequence"; "Slack · #sales" over "Send a message"; "HubSpot · Sync
 * Contact" over "Sync person fields to your CRM"; "HubSpot · Create task"
 * over "This person". Read after nodeDetail's own lines (a company CRM step,
 * a digest or per record Notify). Null for any other step.
 */
export function connectorDetail(
  type: string,
  config: CustomNodeConfig,
  /** The play's trigger, for the CRM Review and Merge duplicates work on. */
  trigger?: Partial<TriggerConfig>
): string | null {
  if (type === 'review_duplicates' || type === 'merge_duplicates')
    return cleanupCrmType(trigger)
      ? (connectorOf(type, config)?.action ?? null)
      : null;
  const row = connectorOf(type, config);
  if (!row?.tool) return null;
  switch (row.stepType) {
    case 'add_to_sequence': {
      const c = config as AddToSequenceConfig;
      return c.sequenceId && c.sequenceName?.trim()
        ? row.action
        : sequenceStepSummary(c);
    }
    case 'notify': {
      const c = config as NotifyConfig;
      return notifyTarget(c)
        ? row.action
        : (nodeSummary('notify', config) ?? row.action);
    }
    case 'crm_action': {
      const c = config as CrmActionConfig;
      if (isCrmWriteAction(c.action))
        return CRM_TARGET_LABELS[c.target ?? 'record'] ?? row.action;
      return c.action === 'lookup'
        ? 'Read person fields from your CRM'
        : 'Sync person fields to your CRM';
    }
    case 'send_mailbox_email': {
      const c = config as SendMailboxEmailConfig;
      return c.mailboxEmail?.trim()
        ? row.action
        : (nodeSummary('send_mailbox_email', config) ?? row.action);
    }
    case 'send_linkedin_action': {
      const c = config as SendLinkedInActionConfig;
      return c.accountId && c.accountName?.trim()
        ? connectorAction(row, config)
        : (nodeSummary('send_linkedin_action', config) ?? row.action);
    }
    default:
      return row.action;
  }
}

/**
 * A step's name where the builder prints one (the step panel, held records,
 * a proposal's removed steps): "Salesloft · Add to cadence", "HubSpot ·
 * Create task" for a tool's step, "Write to list columns" for an Export to
 * the agent's own list, an Export's company list, else its type's label.
 */
export function nodeDisplayName(node: {
  type: CustomNodeType;
  config?: Partial<CustomNodeConfig>;
}): string {
  if (
    node.type === 'export' &&
    ['lead_list', 'company_list'].includes(
      (node.config as Partial<ExportConfig> | undefined)?.destination ?? ''
    )
  )
    return (
      nodeSummary('export', node.config as CustomNodeConfig) ??
      nodeLabel(node.type)
    );
  const row = connectorOf(node.type, node.config);
  return row?.tool
    ? `${row.name} · ${connectorAction(row, node.config)}`
    : nodeLabel(node.type);
}

/**
 * A step's name as the API's `step_headline` gives it, for a list that
 * names live steps beside steps the API names (a proposal's held records
 * beside its removed steps). A tool's step reads as its card headline
 * ("Salesloft · EMEA VP Sales"), a one tool step as its tool and action, any
 * other as its type's label.
 * Pinned to the API by fixtures/connector_headline_parity.json.
 */
export function stepHeadline(
  node: { type: CustomNodeType; config?: Partial<CustomNodeConfig> },
  /** The play's trigger, for the CRM Review and Merge duplicates work on. */
  trigger: Partial<TriggerConfig> | undefined
): string {
  return (
    connectorHeadline(
      node.type,
      (node.config ?? {}) as CustomNodeConfig,
      trigger
    ) ?? nodeDisplayName(node)
  );
}
